import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { isDefined } from "@clipboard-health/util-ts";

import { type CognitoUserCredential } from "./cognitoAuth";
import {
  CognitoSideChannelError,
  revokeTokenSideChannel,
  signInWithPasswordSideChannel,
} from "./cognitoSideChannel";
import { signInWithPassword } from "./cognitoPasswordAuth";

interface SignInWithPasswordPreservingEmailOtpParams {
  email: string;
  password: string;
  onBeforeAmplifySignIn: () => void;
}

/**
 * Checks a password without replacing Amplify's pending EMAIL_OTP challenge.
 * A correct password is then replayed through Amplify so Amplify remains the
 * only owner of the real login session. Probe tokens are discarded and their
 * refresh token is revoked best-effort.
 */
export async function signInWithPasswordPreservingEmailOtp({
  email,
  password,
  onBeforeAmplifySignIn,
}: SignInWithPasswordPreservingEmailOtpParams): Promise<CognitoUserCredential> {
  let probeResult;
  try {
    probeResult = await signInWithPasswordSideChannel({ email, password });
  } catch (error: unknown) {
    if (error instanceof CognitoSideChannelError && error.isNewPasswordRequired) {
      // Re-run the accepted temporary password through Amplify so it owns the
      // NEW_PASSWORD_REQUIRED challenge completed by the login form.
      onBeforeAmplifySignIn();
      return await signInWithPassword(email, password);
    }
    throw error;
  }

  if (isDefined(probeResult.refreshToken)) {
    // Start cleanup immediately but do not await it: revocation failure or the
    // request timeout must never delay the user's real Amplify sign-in.
    void revokeTokenSideChannel({ refreshToken: probeResult.refreshToken }).catch(
      (error: unknown) => {
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { operation: "revoke-side-channel-password-token" },
        });
      }
    );
  }

  onBeforeAmplifySignIn();
  return await signInWithPassword(email, password);
}
